import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { SG_GET_DEBRIEF_RESPONSES, DEBRIEF_RESPONSE_PAGE_NUMBER } from "constants/actions";
import { format } from "date-fns";

// shadcn/ui components
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { Badge } from "components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Separator } from "components/ui/separator";

// Lucide icons
import {
  FileText,
  Loader2,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  History,
  Sparkles,
} from "lucide-react";

import FixSurveyEmpResponse from "./FixSurveyEmpResponse";
import DeleteResponseModal from "./DeleteResponseModal";
import ReactJson from "react-json-view";
import { Button as SemanticButton } from "semantic-ui-react";

const BriefResponseList = ({
  deBriefId,
  employeeId,
  empUpdateRow,
  setEmpUpdateRow,
  searchTerm,
  recent = false,
}) => {
  const dispatch = useDispatch();

  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadPAge, setReloadPage] = useState(false);
  const [activeResponse, setActiveResponse] = useState(null);
  const [activeSummary, setActiveSummary] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteResponseId, setDeleteResponseId] = useState(null);
  const [deleteResponseData, setDeleteResponseData] = useState(null);

  useEffect(() => {
    if (deBriefId) {
      dispatch({
        type: SG_GET_DEBRIEF_RESPONSES,
        payload: `page=${page}${deBriefId ? `&debrief_schedule=${deBriefId}` : ""}${searchTerm ? `&survey_token=${searchTerm}` : ""}`,
      });
    }
    if (employeeId) {
      dispatch({
        type: SG_GET_DEBRIEF_RESPONSES,
        payload: `page=${page}${employeeId ? `&employee=${employeeId}` : ""}`,
      });
    }
    if (recent) {
      dispatch({
        type: SG_GET_DEBRIEF_RESPONSES,
        payload: `page=${page}`,
      });
    }
    setLoading(true);
  }, [dispatch, page, deBriefId, employeeId, empUpdateRow, searchTerm, reloadPAge, recent]);

  const { get_debrief_responses } = useSelector(
    (state) => ({
      get_debrief_responses: state.debrief_response,
    }),
    shallowEqual
  );

  useEffect(() => {
    setResults(get_debrief_responses?.debrief_response?.results || []);
    setPage(get_debrief_responses?.pageNumber || 1);
    const count = get_debrief_responses?.debrief_response?.count || 0;
    const pageSize = get_debrief_responses?.debrief_response?.page_size || 1;
    setTotalPages(count > 0 ? Math.ceil(count / pageSize) : 1);
  }, [get_debrief_responses]);

  const paginationItems = useMemo(() => {
    const pages = Number(totalPages) || 0;
    const current = Number(page) || 1;
    if (pages <= 1) return [];

    const items = [];
    const addPage = (p) => items.push({ type: "page", page: p, active: p === current });
    const addEllipsis = (key) => items.push({ type: "ellipsis", key });

    addPage(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(pages - 1, current + 1);

    if (start > 2) addEllipsis("start");
    for (let p = start; p <= end; p += 1) addPage(p);
    if (end < pages - 1) addEllipsis("end");
    if (pages > 1) addPage(pages);
    return items;
  }, [page, totalPages]);

  function handlePageChange(newPage) {
    setPage(newPage);
    dispatch({
      type: DEBRIEF_RESPONSE_PAGE_NUMBER,
      payload: newPage,
    });
  }

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer1);
  }, [loading]);

  const handleViewResponse = (response) => {
    setActiveResponse(response);
    setActiveSummary(null);
    setViewDialogOpen(true);
  };

  const handleViewSummary = (summary) => {
    setActiveResponse(null);
    setActiveSummary(summary);
    setViewDialogOpen(true);
  };

  const safeFormatDate = (value) => {
    if (!value) return null;
    try {
      return format(new Date(value), "dd-MMM-yyyy HH:mm");
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Survey Responses</CardTitle>
          <CardDescription>
            {results.length ? `${results.length} responses on this page` : "No responses yet"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 rounded-md">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {results.length === 0 && !loading ? (
              <div className="py-12 text-center">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-muted-foreground">No responses found</p>
                <p className="text-xs text-muted-foreground mt-1">
                  No survey responses have been submitted yet
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">ID</TableHead>
                      <TableHead>Survey ID</TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((row) => {
                      const hasModifiedResponse = Boolean(row?.updated_response);
                      const hasMlFeedback = Boolean(row?.ml_feedback);
                      const hasUpdateSummary = Boolean(row?.update_summary);

                      return (
                        <TableRow
                          key={row?.id}
                          className={hasModifiedResponse ? "bg-orange-50/50" : ""}
                        >
                          <TableCell className="font-mono text-sm">{row?.id}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {row?.debrief_schedule || "-"}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {row?.survey_token ? (
                              <span className="truncate max-w-[120px] block" title={row.survey_token}>
                                {row.survey_token}
                              </span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {row?.employee || "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {safeFormatDate(row?.updated_at) || "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {row?.response && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewResponse(row?.response)}
                                  className="h-8"
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  View
                                </Button>
                              )}
                              {hasModifiedResponse && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewResponse(row?.updated_response)}
                                  className="h-8 text-orange-600 border-orange-200 hover:bg-orange-50"
                                  title="Modified Response"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {hasMlFeedback && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewSummary(row?.ml_feedback)}
                                  className="h-8 text-purple-600 border-purple-200 hover:bg-purple-50"
                                  title="NLP Updated"
                                >
                                  <Sparkles className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {hasUpdateSummary && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewSummary(row?.update_summary)}
                                  className="h-8 text-blue-600 border-blue-200 hover:bg-blue-50"
                                  title="Modified History"
                                >
                                  <History className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {!recent && empUpdateRow && (
                                <UpdateResponseButton
                                  responseId={row.id}
                                  setEmpUpdateRow={setEmpUpdateRow}
                                />
                              )}
                              {!recent && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setDeleteResponseId(row?.id);
                                      setDeleteResponseData({
                                        surveyToken: row?.survey_token,
                                        responseId: row?.id,
                                        employeeID: row?.employee,
                                      });
                                    }}
                                    className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                  {deleteResponseId === row?.id && deleteResponseData && (
                                    <DeleteResponseModal
                                      surveyToken={deleteResponseData.surveyToken}
                                      responseId={deleteResponseData.responseId}
                                      employeeID={deleteResponseData.employeeID}
                                      deleteResponse={true}
                                      setDeleteResponse={(open) => {
                                        if (!open) {
                                          setDeleteResponseId(null);
                                          setDeleteResponseData(null);
                                        }
                                      }}
                                      setReloadPage={setReloadPage}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <>
              <Separator className="my-4" />
              <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) handlePageChange(page - 1);
                        }}
                        className={page === 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    {paginationItems.map((item, idx) =>
                      item.type === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${item.key}-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={`page-${item.page}`}>
                          <PaginationLink
                            href="#"
                            isActive={item.active}
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(item.page);
                            }}
                          >
                            {item.page}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    )}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) handlePageChange(page + 1);
                        }}
                        className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Response Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Detailed Response</DialogTitle>
            <DialogDescription>
              {activeResponse ? "View response details" : "View summary details"}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {activeResponse && <ReactJson src={activeResponse} />}
            {activeSummary && <ReactJson src={activeSummary} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BriefResponseList;

// Wrapper component to make FixSurveyEmpResponse button more compact
const UpdateResponseButton = ({ responseId, setEmpUpdateRow }) => {
  return (
    <FixSurveyEmpResponse
      responseId={responseId}
      setEmpUpdateRow={setEmpUpdateRow}
      trigger={
        <SemanticButton positive size="small" compact>
          Update
        </SemanticButton>
      }
    />
  );
};
